import { z } from 'zod';
/**
 * Campaign attribution on a lead — ONE list of keys, shared by every side that
 * touches it (the same reasoning as `HONEYPOT_FIELD_NAME`, review #491 MINOR-4).
 *
 * ── WHY THE CLICK IDS ARE HERE (pre-live gate Ads, 24-9) ───────────────────
 * The five `utm_*` keys say WHICH campaign; they cannot tie a lead back to the
 * Google Ads click. `gclid` (web), `gbraid` (iOS app-to-web) and `wbraid`
 * (iOS web-to-web) are what an offline conversion import matches on, so a lead
 * that is later won can be reported back to Ads as a conversion. Without them
 * the CRM knows the campaign and Ads never learns which click earned money.
 *
 * ── WHY UNKNOWN KEYS ARE DROPPED, NOT REFUSED ──────────────────────────────
 * The attribution object arrives from a public widget and lands verbatim in a
 * `Json` column that the CRM prints. A free-form record would store whatever a
 * crafted URL puts in it (`utm_email=…` is PII smuggling with extra steps).
 * Refusing it with a 400 would instead lose a real lead over a tracking tag, so
 * the schema keeps only the keys below and silently drops the rest — at the
 * edge (which forwards the parsed body) and again at the backend.
 *
 * ── ATTRIBUTION NEVER COSTS A LEAD (RV616b MINOR-3) ────────────────────────
 * Not even a malformed one. A known utm value over 200 characters is CUT (never
 * on half a surrogate pair), a click id over 200 is DROPPED (a cut id matches
 * no click), a foreign key or a value that is not a string is dropped, and a
 * `utm` that is not a plain object is no attribution at all. The bound used to
 * sit in front of the cleaning as a record bound: a 201-character utm, or 195
 * NULs plus six letters, turned the WHOLE intake into a 400. The request size
 * stays bounded by the edge and the body parser, not by this field.
 *
 * ── WHY THE VALUES ARE CLEANED TOO (RV616 m1, m5) ──────────────────────────
 * A known key says nothing about its value, and the value comes out of a URL
 * anybody can write:
 *  - control characters and a listed set of invisible format characters go
 *    (the list is at `istSteuerzeichen`). One NUL (`%00`) made Postgres refuse
 *    the whole lead INSERT (22P05, measured on PG 16.14): the lead was lost
 *    over a tracking tag. A lone surrogate half goes for the same reason
 *    (`pgSafeText` in audit-chain.ts is the house precedent), and CR/LF, C1
 *    and the bidi overrides (U+202E) have no business in a campaign name;
 *  - a utm value is trimmed, and dropped when it looks like an e-mail address
 *    or a phone number: contact data belongs in the lead's own, consented
 *    fields, not in a tracking tag. That is a filter for the obvious cases,
 *    not a guarantee — utm stays personal data under the privacy text;
 *  - a click id is kept only as a plain URL-safe token (`[A-Za-z0-9_-]`). That
 *    is what Google's ids look like; Google publishes no format, so it is an
 *    observation, not a spec. A click id is never cleaned: a changed id matches
 *    no click, and a wrong id in an offline import is worse than none.
 * The widgets and both `embed.js` files apply the same rules before anything
 * leaves the browser; their tests compare them with this file.
 *
 * ── WHAT THIS SCHEMA DOES NOT DECIDE (RV616 n9) ────────────────────────────
 * Whether a click id may ride at all is the visitor's ads choice, and only the
 * page that embeds the widget knows it: the widgets ask that page right before
 * a submit (RV614 M3). A schema cannot ask, so every intake that uses it —
 * `/internal/lead` today, any later one — takes click ids as they come. The
 * backend keeps them out of the database while ATTRIBUTION_KLICK_IDS_AKTIV is
 * off (RV616 m4).
 *
 * Browser bundles must not pull zod, so the widgets MIRROR this list and a
 * Node-side test compares the two (`attribution.test.ts` in immo-map and in
 * calculator-ui); both `public/embed.js` files carry the list too, pinned by
 * their executed `embed-attribution` tests.
 */
export const ATTRIBUTION_SCHLUESSEL = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'gclid',
    'gbraid',
    'wbraid',
];
/** One value's cap — the pre-existing `utm` bound, unchanged. */
export const ATTRIBUTION_WERT_MAX = 200;
/** The Google click ids: the part of the attribution that needs the ads choice. */
export const ATTRIBUTION_KLICK_IDS = ['gclid', 'gbraid', 'wbraid'];
const ERLAUBT = new Set(ATTRIBUTION_SCHLUESSEL);
const KLICK_IDS = new Set(ATTRIBUTION_KLICK_IDS);
const KLICK_ID_FORMAT = /^[A-Za-z0-9_-]+$/;
const SIEHT_AUS_WIE_EMAIL = /[^\s@]@[^\s@]+\.[^\s@]{2,}/;
/** `+41 79 …`, `0041…`, `079 …`: a leading `+` or `0`, then 9 or more digits. */
const SIEHT_AUS_WIE_TELEFON = /(?:^|\D)(?:\+|0)\d(?:[\s./()-]{0,2}\d){7,}/;
/**
 * C0 (NUL, TAB, CR, LF …), DEL and C1, and these invisible format characters
 * (RV616b n-c): the soft hyphen U+00AD, the Arabic letter mark U+061C, the
 * zero-width space U+200B, the bidi marks LRM/RLM, the line and paragraph
 * separators, the bidi embeddings, overrides and isolates, the word joiner and
 * the invisible operators U+2060–U+2064, and the BOM. ZWNJ and ZWJ (U+200C,
 * U+200D) stay: emoji sequences and some scripts need them. Char codes, not a
 * regex class, for the reason `pgSafeText` gives: a control character in a
 * class is a lint error.
 *
 * Exported since B11 (RV-B11 MINOR-2): the IAZI hints (`avmDataInfo.hinweise`,
 * schemas/valuation.ts) use THIS set — one house set for one invariant, not a
 * second list that drifts.
 */
export function istSteuerzeichen(c) {
    return (c <= 0x1f ||
        (c >= 0x7f && c <= 0x9f) ||
        c === 0xad ||
        c === 0x061c ||
        c === 0x200b ||
        c === 0x200e ||
        c === 0x200f ||
        (c >= 0x2028 && c <= 0x202e) ||
        (c >= 0x2060 && c <= 0x2064) ||
        (c >= 0x2066 && c <= 0x2069) ||
        c === 0xfeff);
}
/** Without control characters, and without a surrogate half that has no partner. */
function ohneSteuerzeichen(wert) {
    let aus = '';
    for (let i = 0; i < wert.length; i += 1) {
        const c = wert.charCodeAt(i);
        if (c >= 0xd800 && c <= 0xdbff) {
            const n = wert.charCodeAt(i + 1);
            if (n >= 0xdc00 && n <= 0xdfff) {
                aus += wert.charAt(i) + wert.charAt(i + 1);
                i += 1;
            }
            continue;
        }
        if ((c >= 0xdc00 && c <= 0xdfff) || istSteuerzeichen(c))
            continue;
        aus += wert.charAt(i);
    }
    return aus;
}
/**
 * One value as it may be stored, or `undefined` when nothing usable is left
 * (the rules are in the block comment at the top). The widgets mirror it.
 */
export function bereinigeAttributionsWert(schluessel, wert) {
    if (KLICK_IDS.has(schluessel))
        return KLICK_ID_FORMAT.test(wert) ? wert : undefined;
    const s = ohneSteuerzeichen(wert).trim();
    if (!s || SIEHT_AUS_WIE_EMAIL.test(s) || SIEHT_AUS_WIE_TELEFON.test(s))
        return undefined;
    return s;
}
/** A cut never ends on half a surrogate pair: Postgres (and Prisma) refuse a lone one. */
function ganzeZeichen(s) {
    const c = s.charCodeAt(s.length - 1);
    return c >= 0xd800 && c <= 0xdbff ? s.slice(0, -1) : s;
}
/**
 * Keep the known keys with a usable value; `undefined` when nothing is left,
 * so an all-foreign object is stored as NULL rather than as `{}`. Over the cap
 * a utm tag is cut and a click id dropped (RV616b MINOR-3), exactly like the
 * widget capture.
 */
export function normalisiereAttribution(roh) {
    if (!roh)
        return undefined;
    const aus = {};
    for (const [k, v] of Object.entries(roh)) {
        if (!ERLAUBT.has(k) || typeof v !== 'string')
            continue;
        const wert = bereinigeAttributionsWert(k, v);
        if (!wert)
            continue;
        if (wert.length > ATTRIBUTION_WERT_MAX) {
            if (KLICK_IDS.has(k))
                continue;
            aus[k] = ganzeZeichen(wert.slice(0, ATTRIBUTION_WERT_MAX));
        }
        else {
            aus[k] = wert;
        }
    }
    return Object.keys(aus).length > 0 ? aus : undefined;
}
/** Only a plain object can carry attribution; anything else is none — never a 400. */
function alsObjekt(v) {
    return v !== null && typeof v === 'object' && !Array.isArray(v)
        ? v
        : undefined;
}
/**
 * The wire shape of `utm` on every public intake (lead, valuation, beratung).
 * Any input is accepted and none refused (RV616b MINOR-3, see the top); the
 * output is the whitelisted, cleaned, bounded, typed object.
 *
 * `.optional()` sits OUTSIDE the transform on purpose: inside it, zod makes the
 * key REQUIRED in the output type (`utm: Attribution | undefined`), and every
 * hand-built intake object without `utm` stops compiling (measured with
 * `typecheck:tests`: TS2719 in the ingest tests). Outside, the key stays
 * optional in both directions.
 */
export const attributionSchema = z
    .unknown()
    .transform((v) => normalisiereAttribution(alsObjekt(v)))
    .optional();
